import React from 'react'
import type { WorkflowNodeData } from '@/types/workflow'

interface RateLimitConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

import { inputClass, inputStyle, sectionClass, warnBannerClass } from './_kit'
import { Select } from '@/components/ui/Select'
import { Field } from '@/components/ui/Field'

const MODE_OPTIONS = [
  { value: 'daily_cap', label: 'Daily Cap' },
  { value: 'hourly_cap', label: 'Hourly Cap' },
  { value: 'random_delay', label: 'Random Delay' },
]

const RateLimitConfig: React.FC<RateLimitConfigProps> = ({ data, updateField }) => {
  const config = data.rate_limit_config || { mode: 'daily_cap', max_per_day: 50 }
  const updateConfig = (key: string, value: any) => {
    updateField('rate_limit_config', { ...config, [key]: value })
  }
  // Keep the field empty while the user is typing, and strip leading zeros
  // from the input itself: React's loose value comparison ("05" == 5) would
  // otherwise leave the stale zero visible. Blur snaps back to a valid number
  // so the saved config is always numeric.
  const onNumberChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    if (raw === '') {
      updateConfig(key, '')
      return
    }
    const value = parseInt(raw)
    if (Number.isNaN(value)) return
    const normalized = String(value)
    if (raw !== normalized) e.target.value = normalized
    updateConfig(key, value)
  }
  const onNumberBlur = (key: string, fallback: number, min: number) => () => {
    const raw = (config as any)[key]
    const value = typeof raw === 'number' ? raw : parseInt(raw)
    updateConfig(key, Number.isNaN(value) ? fallback : Math.max(min, value))
  }

  return (
    <div className={sectionClass}>
      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="Rate Limit"
            className={inputClass} style={inputStyle}
          />
        )}
      </Field>

      <Field label="Mode">
        {(f) => (
          <Select
            {...f}
            value={config.mode || 'daily_cap'}
            onChange={(value) => updateConfig('mode', value)}
            options={MODE_OPTIONS}
            className="w-full"
          />
        )}
      </Field>

      {config.mode === 'daily_cap' && (
        <Field label="Max Per Day">
          {(f) => (
            <input
              {...f}
              type="number"
              value={config.max_per_day ?? 50}
              onChange={onNumberChange('max_per_day')}
              onBlur={onNumberBlur('max_per_day', 50, 1)}
              min={1}
              className={inputClass} style={inputStyle}
            />
          )}
        </Field>
      )}

      {config.mode === 'hourly_cap' && (
        <Field label="Max Per Hour">
          {(f) => (
            <input
              {...f}
              type="number"
              value={config.max_per_hour ?? 10}
              onChange={onNumberChange('max_per_hour')}
              onBlur={onNumberBlur('max_per_hour', 10, 1)}
              min={1}
              className={inputClass} style={inputStyle}
            />
          )}
        </Field>
      )}

      {config.mode === 'random_delay' && (
        <>
          <Field label="Min Delay (Minutes)">
            {(f) => (
              <input
                {...f}
                type="number"
                value={config.min_delay_minutes ?? 1}
                onChange={onNumberChange('min_delay_minutes')}
                onBlur={onNumberBlur('min_delay_minutes', 1, 0)}
                min={0}
                className={inputClass} style={inputStyle}
              />
            )}
          </Field>
          <Field label="Max Delay (Minutes)">
            {(f) => (
              <input
                {...f}
                type="number"
                value={config.max_delay_minutes ?? 5}
                onChange={onNumberChange('max_delay_minutes')}
                onBlur={onNumberBlur('max_delay_minutes', 5, 0)}
                min={0}
                className={inputClass} style={inputStyle}
              />
            )}
          </Field>
        </>
      )}

      <div className={warnBannerClass}>
        <p className="text-xs">
          Controls the sending rate to protect email deliverability.
        </p>
      </div>
    </div>
  )
}

export default RateLimitConfig
